'use client';

import { useState, type ReactNode } from 'react';
import Image from 'next/image';

export type SpectrogramPanel = {
  id: string;
  src: string;
  label: string;
};

type SpectrogramViewerProps = {
  title: string;
  subtitle: string;
  panels: SpectrogramPanel[];
  defaultView?: string;
  aspectClass: string;
  freqMax?: number;
  freqTicks?: number[];
  colorbarGradient: string;
  colorbarMax: number;
  colorbarTicks: number[];
  colorbarFormat?: (v: number) => string;
  colorbarLabel?: string;
  extraControl?: ReactNode;
};

const DEFAULT_FREQ_MAX = 22050;
const DEFAULT_FREQ_TICKS = [0, 5000, 10000, 15000, 20000];

const TICK_STYLE = {
  fontFamily: 'Arial, sans-serif',
  fontSize: 13,
  color: 'rgba(255,255,255,0.85)',
} as const;

const AXIS_TITLE_STYLE = {
  fontFamily: 'Arial, sans-serif',
  fontSize: 14,
  color: 'rgba(255,255,255,0.9)',
} as const;

export default function SpectrogramViewer({
  title,
  subtitle,
  panels,
  defaultView,
  aspectClass,
  freqMax = DEFAULT_FREQ_MAX,
  freqTicks = DEFAULT_FREQ_TICKS,
  colorbarGradient,
  colorbarMax,
  colorbarTicks,
  colorbarFormat = (v) => v.toFixed(3),
  colorbarLabel = 'STFT',
  extraControl,
}: SpectrogramViewerProps) {
  const [view, setView] = useState<string>(defaultView ?? panels[0]?.id);

  return (
    <div className="w-full rounded-2xl border border-[#373739] p-4 sm:p-6">
      <div className="text-lg font-bold text-white">{title}</div>
      <div className="mb-4 mt-1.5 text-base text-zinc-400">{subtitle}</div>

      <div className="flex w-full items-stretch">
        <div className="flex shrink-0">
          <div className="relative w-5">
            <span
              className="absolute left-0 top-1/2 -translate-y-1/2 -rotate-90"
              style={AXIS_TITLE_STYLE}
            >
              kHz
            </span>
          </div>
          <div className="relative w-7">
            {freqTicks.map((f) => (
              <span
                key={f}
                className="absolute right-1.5 translate-y-1/2 tabular-nums"
                style={{ bottom: `${(f / freqMax) * 100}%`, ...TICK_STYLE }}
              >
                {f / 1000}
              </span>
            ))}
          </div>
        </div>

        <div className={`relative flex-1 overflow-hidden rounded-lg ${aspectClass}`}>
          {panels.map((p) => (
            <Image
              key={p.id}
              src={p.src}
              alt={`${p.label} STFT spectrogram`}
              fill
              className={`object-cover transition-opacity duration-300 ${
                p.id === view ? 'opacity-100' : 'opacity-0'
              }`}
              sizes="(max-width: 640px) 100vw, 800px"
              priority={p.id === (defaultView ?? panels[0]?.id)}
            />
          ))}
        </div>

        <div className="flex shrink-0">
          <div className="my-px ml-2 w-2.5 shrink-0 rounded-sm" style={{ background: colorbarGradient }} />
          <div className="relative w-12">
            {colorbarTicks.map((v) => (
              <span
                key={v}
                className="absolute left-1.5 translate-y-1/2 tabular-nums"
                style={{ bottom: `${(v / colorbarMax) * 100}%`, ...TICK_STYLE }}
              >
                {colorbarFormat(v)}
              </span>
            ))}
          </div>
          <div className="relative w-5">
            <span
              className="absolute right-0 top-1/2 -translate-y-1/2 rotate-90"
              style={AXIS_TITLE_STYLE}
            >
              {colorbarLabel}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-[#373739] pt-4 text-xs">
        <div className="flex items-center gap-2">
          <span className="text-zinc-500">View</span>
          <div className="flex items-center gap-1 rounded-lg bg-white/5 p-0.5">
            {panels.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setView(p.id)}
                className={`rounded-md px-2.5 py-1 transition ${
                  view === p.id ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-white'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        {extraControl}
      </div>
    </div>
  );
}
